/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import { useState, useMemo, useEffect, useCallback } from 'react';
import { DialogFormConfig } from '../types/DialogTypes';

interface UseDialogFormProps<T> {
    /**
    * Configuration object containing required fields and default values
     */
    config: DialogFormConfig<T>;
    
    /**
     * Initial values passed from parent (e.g., when editing an existing entity)
    * These will be merged with config.defaultValues
     */
    initialValues?: Partial<T>;
    
    /**
     * Whether the dialog is currently open
     * Used to trigger auto-reset when dialog closes
     */
    isOpen: boolean;
}

interface UseDialogFormReturn<T> {
    /**
     * Current form field values
     */
    fields: T;
    
    /**
     * Handler to update a specific field value
     */
    handleChange: <K extends keyof T>(key: K, value: T[K]) => void;
    
    /**
     * Whether the form has been modified from initial values
     */
    isDirty: boolean;
    
    /**
     * Whether all required fields have valid (non-empty) values
     */
    hasValidRequiredFields: boolean;

    /**
     * Required fields that are currently missing/invalid
     */
    missingRequiredFields: ReadonlyArray<keyof T>;
    
    /**
     * Reset form fields to initial values
     */
    resetFields: () => void;
    
    /**
     * Validates that all required fields are filled
     * Returns true if valid, false otherwise
     */
    validateRequiredFields: () => boolean;

    /**
     * Validates form and marks missing required fields as touched
     */
    validateForm: () => boolean;

    /**
     * Marks a field as touched (or untouched)
     */
    setFieldTouched: <K extends keyof T>(key: K, touched?: boolean) => void;

    /**
     * Whether a field should display required-field error state
     */
    shouldShowFieldError: <K extends keyof T>(key: K) => boolean;

    /**
     * Returns fields with empty/whitespace-only strings replaced by undefined,
     * suitable for use in API payloads (JSON.stringify omits undefined values).
     */
    getPayload: () => Partial<T>;
}

/**
 * Custom hook for managing dialog form state, validation, and dirty tracking
 * 
 * @template T - Type of the form fields object
 * @param props - Configuration and state props
 * @returns Form state and handlers
 * 
 * @example
 * ```tsx
 * const { fields, handleChange, isDirty, hasValidRequiredFields, validateRequiredFields } = 
 *   useDialogForm({
 *     config: {
 *       requiredFields: ['name', 'email'],
 *       defaultValues: { name: '', email: '', phone: '' }
 *     },
 *     initialValues: props.initialValues,
 *     isOpen: props.open
 *   });
 * ```
 */
export function useDialogForm<T extends Record<string, unknown>>({
    config,
    initialValues,
    isOpen,
}: UseDialogFormProps<T>): UseDialogFormReturn<T> {
    // Merge default values with provided initial values
    const mergedInitial = useMemo(() => ({
        ...config.defaultValues,
        ...initialValues,
    }), [config.defaultValues, initialValues]);

    const [fields, setFields] = useState<T>(mergedInitial);
    const [touchedFields, setTouchedFields] = useState<Partial<Record<keyof T, boolean>>>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    // Reset fields when dialog opens or initial values change
    useEffect(() => {
        if (isOpen) {
            setFields(mergedInitial);
            setTouchedFields({});
            setSubmitAttempted(false);
        }
    }, [isOpen, mergedInitial]);

    // Auto-reset when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setFields(config.defaultValues);
            setTouchedFields({});
            setSubmitAttempted(false);
        }
    }, [isOpen, config.defaultValues]);

    // Handler for updating individual field values
    const handleChange = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
        setFields(prev => ({ ...prev, [key]: value }));
    }, []);

    const isRequiredFieldFilled = useCallback((value: T[keyof T]) => {
        if (value === null || value === undefined) {
            return false;
        }

        if (typeof value === 'string') {
            return value.trim() !== '';
        }

        return value !== '';
    }, []);

    // Check if form has been modified from initial values
    const isDirty = useMemo(() => {
        return JSON.stringify(fields) !== JSON.stringify(mergedInitial);
    }, [fields, mergedInitial]);

    const missingRequiredFields = useMemo(() => {
        return config.requiredFields.filter(field => !isRequiredFieldFilled(fields[field]));
    }, [fields, config.requiredFields, isRequiredFieldFilled]);

    const missingRequiredFieldsSet = useMemo(() => {
        return new Set(missingRequiredFields);
    }, [missingRequiredFields]);

    // Check if all required fields have valid values
    const hasValidRequiredFields = useMemo(() => {
        const requiredFieldsValid = missingRequiredFields.length === 0;

        // If there's additional validation, apply it
        if (config.additionalValidation) {
            return requiredFieldsValid && config.additionalValidation(fields);
        }

        return requiredFieldsValid;
    }, [fields, missingRequiredFields, config.additionalValidation]);

    // Validation function that can be called before form submission
    const validateRequiredFields = useCallback(() => {
        if (missingRequiredFields.length > 0) {
            return false;
        }

        // Apply additional validation if provided
        if (config.additionalValidation) {
            return config.additionalValidation(fields);
        }

        return true;
    }, [fields, missingRequiredFields, config.additionalValidation]);

    const setFieldTouched = useCallback(<K extends keyof T>(key: K, touched = true) => {
        setTouchedFields(prev => ({ ...prev, [key]: touched }));
    }, []);

    const shouldShowFieldError = useCallback(<K extends keyof T>(key: K) => {
        return missingRequiredFieldsSet.has(key) && (!!touchedFields[key] || submitAttempted);
    }, [missingRequiredFieldsSet, touchedFields, submitAttempted]);

    const validateForm = useCallback(() => {
        setSubmitAttempted(true);

        if (missingRequiredFields.length > 0) {
            setTouchedFields(prev => {
                const updated = { ...prev };
                for (const field of missingRequiredFields) {
                    updated[field] = true;
                }
                return updated;
            });
            return false;
        }

        return validateRequiredFields();
    }, [missingRequiredFields, validateRequiredFields]);

    const getPayload = useCallback((): Partial<T> => {
        return Object.fromEntries(
            Object.entries(fields as Record<string, unknown>).map(([key, value]) => [
                key,
                typeof value === 'string' && value.trim() === '' ? undefined : value,
            ])
        ) as Partial<T>;
    }, [fields]);

    // Manual reset function
    const resetFields = useCallback(() => {
        setFields(mergedInitial);
        setTouchedFields({});
        setSubmitAttempted(false);
    }, [mergedInitial]);

    return {
        fields,
        handleChange,
        isDirty,
        hasValidRequiredFields,
        missingRequiredFields,
        resetFields,
        validateRequiredFields,
        validateForm,
        setFieldTouched,
        shouldShowFieldError,
        getPayload,
    };
}
